/**
 * Device-room frame codec — the browser twin of `crates/rpc/src/device_room.rs`
 * and `edge/src/device-room.ts`.
 *
 * Wire shape: `uleb128(header_len) ‖ UTF-8 JSON header ‖ payload`, header
 * `{s, k, to?, from?}`. The length prefix counts UTF-8 *bytes* of the header,
 * and `JSON.stringify` key order must stay `s, k, to, from` with absent routing
 * keys omitted (never `null`) — that is what makes the browser byte-compatible
 * with the Rust host.
 *
 * Test vectors below are copied verbatim from the Rust unit tests
 * (`device_room.rs` `round_trips_header_and_payload`,
 * `handles_empty_payloads_and_long_headers`, `byte_parity_with_ts_encoder`,
 * `rejects_malformed_frames`) and from `edge/src/device-frame.test.ts`, so the
 * two implementations cannot drift apart silently.
 */

export interface DeviceFrameHeader {
  /** Stream id, unique per (connId, logical stream). */
  s: string;
  /** Stream kind: `"rpc"` | `"echo"` | … — opaque to the relay. */
  k: string;
  /** Routing: host → client target. */
  to?: string;
  /** Routing: client → host origin (stamped by the relay). */
  from?: string;
}

/** Relay-emitted control frames. The leading space in `" relay"` is real: the
 * relay and both Rust ends compare with `===`, so a "fixed" spelling makes
 * host_offline/host_closed invisible. */
export const RELAY_KIND = " relay";
/** Durable command nudge (`{chatId, token?}`) — edge → host delivery hints.
 * A browser should ignore them; the host drains the work. */
export const NUDGE_KIND = "nudge";
/** The RPC stream over the relay: `s` and `k` are both `"rpc"`. */
export const RPC_KIND = "rpc";
/** Application-level end-to-end liveness. Browsers cannot send WebSocket
 * control pings, so `echo` is the only signal that proves the edge↔host leg. */
export const ECHO_KIND = "echo";

/** Relay error codes carried as `{"error": code}` on a [`RELAY_KIND`] frame. */
export const HOST_OFFLINE = "host_offline";
export const HOST_CLOSED = "host_closed";
export const CLIENT_GONE = "client_gone";
export const CLIENT_CLOSED = "client_closed";

export class DeviceFrameError extends Error {}

/** Encode a non-negative integer as ULEB128. */
export const encodeUleb128 = (value: number): number[] => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new DeviceFrameError(`uleb128: not a non-negative safe integer: ${value}`);
  }
  const out: number[] = [];
  let n = value;
  for (;;) {
    const byte = n % 0x80;
    n = Math.floor(n / 0x80);
    out.push(n === 0 ? byte : byte | 0x80);
    if (n === 0) return out;
  }
};

/** Decode ULEB128, mirroring the Rust bounds: 5 bytes max (shift < 32). */
export const decodeUleb128 = (
  bytes: Uint8Array,
  offset = 0
): { value: number; next: number } => {
  let value = 0;
  let shift = 0;
  let at = offset;
  for (;;) {
    if (at >= bytes.length) throw new DeviceFrameError("truncated uleb128");
    const byte = bytes[at] as number;
    at += 1;
    if (shift >= 32) throw new DeviceFrameError("uleb128 overflow");
    // Multiplication, not `<<`: JS shifts are 32-bit and would wrap at 2^31.
    value += (byte & 0x7f) * 2 ** shift;
    if ((byte & 0x80) === 0) return { value, next: at };
    shift += 7;
  }
};

/** Serialize the header with the exact key order the Rust encoder produces. */
export const encodeHeader = (header: DeviceFrameHeader): string => {
  const ordered: Record<string, string> = { s: header.s, k: header.k };
  if (header.to !== undefined) ordered.to = header.to;
  if (header.from !== undefined) ordered.from = header.from;
  return JSON.stringify(ordered);
};

/** Encode `uleb128(header_len) ‖ header JSON ‖ payload`. */
export const encodeDeviceFrame = (
  header: DeviceFrameHeader,
  payload: Uint8Array = new Uint8Array()
): Uint8Array => {
  const headerBytes = new TextEncoder().encode(encodeHeader(header));
  const prefix = encodeUleb128(headerBytes.length);
  const out = new Uint8Array(prefix.length + headerBytes.length + payload.length);
  out.set(prefix, 0);
  out.set(headerBytes, prefix.length);
  out.set(payload, prefix.length + headerBytes.length);
  return out;
};

/** Encode a UTF-8 text payload (the RPC path: one NDJSON envelope per frame). */
export const encodeTextFrame = (header: DeviceFrameHeader, text: string): Uint8Array =>
  encodeDeviceFrame(header, new TextEncoder().encode(text));

/** Decode a device frame; the payload is every byte after the header. */
export const decodeDeviceFrame = (
  bytes: Uint8Array
): { header: DeviceFrameHeader; payload: Uint8Array } => {
  const { value: headerLen, next } = decodeUleb128(bytes);
  const headerEnd = next + headerLen;
  if (headerEnd > bytes.length) throw new DeviceFrameError("truncated header");
  const headerJson = new TextDecoder().decode(bytes.subarray(next, headerEnd));
  let parsed: unknown;
  try {
    parsed = JSON.parse(headerJson);
  } catch {
    throw new DeviceFrameError("bad header JSON");
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new DeviceFrameError("header is not an object");
  }
  const raw = parsed as Record<string, unknown>;
  if (typeof raw.s !== "string" || typeof raw.k !== "string") {
    throw new DeviceFrameError("header missing s/k");
  }
  const header: DeviceFrameHeader = { s: raw.s, k: raw.k };
  if (typeof raw.to === "string") header.to = raw.to;
  if (typeof raw.from === "string") header.from = raw.from;
  return { header, payload: bytes.subarray(headerEnd) };
};

/** Extract the error code from a relay control payload (`{"error": code}`). */
export const relayErrorCode = (payload: Uint8Array): string | undefined => {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(payload)) as { error?: unknown };
    return typeof parsed?.error === "string" ? parsed.error : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Dial URL for a browser client link. Mirrors `device_room_ws_url` in Rust:
 * `{ws_base}/device/{deviceId}/ws?role=client&connId={id}&token={token}`.
 * The token is a short-lived access token, never a refresh token — it can end
 * up in proxy and edge logs, so it must expire quickly.
 */
export const deviceRoomWsUrl = (options: {
  edgeUrl: string;
  deviceId: string;
  role?: "client" | "host";
  connId?: string;
  token: string;
}): string => {
  const base = options.edgeUrl.replace(/^http/, "ws").replace(/\/+$/, "");
  const params = new URLSearchParams({ role: options.role ?? "client" });
  if (options.connId) params.set("connId", options.connId);
  params.set("token", options.token);
  return `${base}/device/${encodeURIComponent(options.deviceId)}/ws?${params.toString()}`;
};
